import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import { fetchAdminOrders, AdminOrderSummary } from '../adminService';
import { useAdminSession } from '../adminSession';
import { ApiError } from '../../../services/apiClient';
import { formatPhone } from '../../../utils/formatPhone';
import { ORDER_STATUS_LABEL, OrderStatus } from '../../../types/order';
import { fetchPortfolioItems } from '../../Portfolio/portfolioService';
import { PortfolioItem } from '../../Portfolio/portfolio.types';

const THUMB_CLASSES = ['b1', 'b2', 'b3', 'b4', 'b5'];
const PORTFOLIO_IMG_CLASSES = ['b1', 'b2', 'b3'];
// 대시보드 "최근 등록된 포트폴리오" 섹션 노출 여부 (다시 보이게 하려면 true)
const SHOW_RECENT_PORTFOLIO = false;
// 상단 "오늘의 할 일 · 미처리 견적과 검토 대기 시안" 카드 노출 여부 (다시 보이게 하려면 true)
const SHOW_TODAY_TODO = false;
// 상단 파란 카드 아래 지표(자체 공장·이번 달 생산·평균 납기·재구매 고객) 노출 여부 (다시 보이게 하려면 true)
const SHOW_HERO_META = false;

const ORDER_STATUS_BADGE: Record<OrderStatus, 'ok' | 'warn' | 'info' | 'neutral' | 'danger'> = {
  PENDING_QUOTE: 'warn',
  QUOTE_CONFIRMED: 'info',
  PENDING_PAYMENT: 'warn',
  PAYMENT_COMPLETED: 'info',
  PROOF_IN_PROGRESS: 'info',
  PROOF_FEEDBACK: 'warn',
  PROOF_APPROVED: 'ok',
  PRODUCTION: 'ok',
  SHIPPED: 'ok',
  COMPLETED: 'neutral',
  CANCELLED: 'danger',
};

// 원 → 만원 단위 (예: 48,200,000 → "4,820")
function formatManwon(won: number): string {
  return Math.round(won / 10_000).toLocaleString();
}

// 대시보드 선 아이콘 (색은 글자색을 따름)
const DASH_ICONS = {
  inquiry: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  order: (
    <>
      <path d="M21 8 12 3 3 8v8l9 5 9-5V8z" />
      <path d="m3 8 9 5 9-5M12 13v8" />
    </>
  ),
  sample: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  revenue: (
    <>
      <path d="M19 7V5a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v3" />
      <path d="M3 6v12a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-3" />
      <path d="M17 12h4v4h-4a2 2 0 0 1 0-4z" />
    </>
  ),
  trendUp: (
    <>
      <path d="m3 17 6-6 4 4 8-8" />
      <path d="M15 7h6v6" />
    </>
  ),
  trendDown: (
    <>
      <path d="m3 7 6 6 4-4 8 8" />
      <path d="M15 17h6v-6" />
    </>
  ),
  person: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0 1 16 0" />
    </>
  ),
};

function DashIcon({ name }: { name: keyof typeof DASH_ICONS }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      {DASH_ICONS[name]}
    </svg>
  );
}

// 도넛 차트: 연회색 바탕 링 위에 조각을 얇은 간격으로 이어 붙임. 비율 합계는 100
const DONUT_SEGMENTS = [
  { name: '시딩 패키지', value: '2,260만', pct: 47, color: 'var(--c-1)' },
  { name: '쿠션 기프트 세트', value: '1,100만', pct: 23, color: 'var(--c-2)' },
  { name: '에코 팩', value: '950만', pct: 20, color: 'var(--c-3)' },
  { name: '맞춤 상담 패키지', value: '510만', pct: 10, color: 'var(--c-4)' },
];
const DONUT_R = 56;
const DONUT_LEN = 2 * Math.PI * DONUT_R;
const DONUT_GAP = 1.5; // 조각 사이 간격

function AdminDashboard() {
  const [session, setSession] = useAdminSession();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<AdminOrderSummary[] | null>(null);
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchAdminOrders(session.token)
      .then(setOrders)
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          setSession(null);
        } else {
          setError(err instanceof Error ? err.message : '대시보드 데이터를 불러오지 못했습니다.');
        }
      });
  }, [session, setSession]);

  useEffect(() => {
    fetchPortfolioItems()
      .then(setPortfolioItems)
      .catch(() => setPortfolioItems([]));
  }, []);

  const newInquiryCount = useMemo(
    () => (orders ?? []).filter((o) => o.status === 'PENDING_QUOTE').length,
    [orders],
  );
  const inProgressOrderCount = useMemo(
    () => (orders ?? []).filter((o) => o.status !== 'COMPLETED' && o.status !== 'CANCELLED').length,
    [orders],
  );
  const monthlyRevenue = useMemo(() => {
    if (!orders) return 0;
    const now = new Date();
    return orders
      .filter((o) => {
        const d = new Date(o.created_at);
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      })
      .reduce((sum, o) => sum + (o.total_price ?? 0), 0);
  }, [orders]);

  const recentOrders = (orders ?? []).slice(0, 6);
  const recentInquiries = (orders ?? []).filter((o) => o.status === 'PENDING_QUOTE').slice(0, 4);
  const maxInquiryQty = Math.max(1, ...recentInquiries.map((i) => i.quantity));
  const recentPortfolio = portfolioItems.slice(0, 3);

  return (
    <AdminLayout title="대시보드">
      {error && (
        <div className="section" style={{ color: '#B91C1C', fontSize: 13 }}>
          {error}
        </div>
      )}

      {/* HERO */}
      <div className={`hero ${SHOW_TODAY_TODO ? '' : 'hero-single'}`}>
        <div className="hero-main">
          <div className="hero-eyebrow">
            <span className="pulse"></span> 실시간 · 오늘 {newInquiryCount + inProgressOrderCount}건 진행 중
          </div>
          <div className="hero-title">
            브랜드의 가치를 <em>패키지</em>로 완성합니다
          </div>
          <div className="hero-desc">
            맞춤 상담부터 시안·샘플, 책임 생산·납품까지 — 자체 공장 운영으로 정직한 가격과 빠른 납기를 보장합니다.
          </div>
          {SHOW_HERO_META && (
            <div className="hero-meta">
              <div className="m">
                <span className="lbl">자체 공장</span>
                <span className="val">운영 중</span>
              </div>
              <div className="m">
                <span className="lbl">이번 달 생산</span>
                <span className="val">128박스</span>
              </div>
              <div className="m">
                <span className="lbl">평균 납기</span>
                <span className="val">7.2 일</span>
              </div>
              <div className="m">
                <span className="lbl">재구매 고객</span>
                <span className="val">62%</span>
              </div>
            </div>
          )}
        </div>

        {SHOW_TODAY_TODO && (
        <div className="hero-side">
          <div>
            <div className="ttl">오늘의 할 일 · 4건</div>
            <h3>미처리 견적과 검토 대기 시안</h3>
          </div>
          <div className="todo-list">
            <div className="todo">
              <div className="chk"></div>
              <div className="txt">
                <strong>샘플 제작 승인 요청</strong> · 신규 거래처
              </div>
              <div className="meta">14:00</div>
            </div>
            <div className="todo">
              <div className="chk done"></div>
              <div className="txt" style={{ textDecoration: 'line-through', color: 'var(--ink-500)' }}>
                <strong>시안 2차 회신</strong> · 메종드프레쉬
              </div>
              <div className="meta done">
                완료
              </div>
            </div>
            <div className="todo">
              <div className="chk"></div>
              <div className="txt">
                <strong>견적 재발송</strong> · 최근 문의 고객사
              </div>
              <div className="meta">16:30</div>
            </div>
            <div className="todo">
              <div className="chk"></div>
              <div className="txt">
                <strong>3D 렌더링 검수</strong> · 진행 중 주문
              </div>
              <div className="meta">18:00</div>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* KPI ROW */}
      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi-icon">
            <DashIcon name="inquiry" />
          </div>
          <span className="kpi-lbl">신규 견적 문의</span>
          <div className="kpi-val">
            {orders ? newInquiryCount : '-'}
            <small>건</small>
          </div>
          <div className="kpi-foot">
            <span className="delta up">
              <DashIcon name="trendUp" />
              접수 대기
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-icon">
            <DashIcon name="order" />
          </div>
          <span className="kpi-lbl">진행 중 주문</span>
          <div className="kpi-val">
            {orders ? inProgressOrderCount : '-'}
            <small>건</small>
          </div>
          <div className="kpi-foot">
            <span className="delta up">
              <DashIcon name="trendUp" />
              전체 {orders?.length ?? 0}건 중
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-icon">
            <DashIcon name="sample" />
          </div>
          <span className="kpi-lbl">샘플 대기</span>
          <div className="kpi-val">
            9<small>건</small>
          </div>
          <div className="kpi-foot">
            <span className="delta down">
              <DashIcon name="trendDown" />
              2건 처리 완료
            </span>
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-icon">
            <DashIcon name="revenue" />
          </div>
          <span className="kpi-lbl">이번 달 매출</span>
          <div className="kpi-val">
            {orders ? formatManwon(monthlyRevenue) : '-'}
            <small>만원</small>
          </div>
          <div className="kpi-foot">
            <span className="delta up">
              <DashIcon name="trendUp" />
              이번 달 누적
            </span>
          </div>
        </div>
      </div>

      {/* CHARTS ROW */}
      <div className="section grid-2">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">월별 견적 문의 추이</div>
              <div className="card-sub">지난 6개월 · 견적 vs 체결률</div>
            </div>
            <div className="seg">
              <span className="seg-item active">6개월</span>
              <span className="seg-item">1년</span>
              <span className="seg-item">전체</span>
            </div>
          </div>
          {/* 선 그래프: 0~120건 → y 200~30, 6개월 → x 60~690 */}
          <div className="line-chart">
            <svg viewBox="0 0 720 240" width="100%">
              <defs>
                <linearGradient id="gwarea" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#2558E8" stopOpacity=".18" />
                  <stop offset="100%" stopColor="#2558E8" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="gwband" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#2558E8" stopOpacity=".16" />
                  <stop offset="100%" stopColor="#2558E8" stopOpacity="0" />
                </linearGradient>
              </defs>
              <g stroke="#E6EAF2" strokeWidth="1" strokeDasharray="4 5">
                <line x1="48" y1="30" x2="700" y2="30" />
                <line x1="48" y1="87" x2="700" y2="87" />
                <line x1="48" y1="143" x2="700" y2="143" />
              </g>
              <line x1="48" y1="200" x2="700" y2="200" stroke="#E6EAF2" strokeWidth="1" />
              <g fontSize="14" fill="#9AA1AC" fontFamily="Pretendard">
                <text x="0" y="35">120</text>
                <text x="8" y="92">80</text>
                <text x="8" y="148">40</text>
                <text x="18" y="205">0</text>
              </g>
              {/* 강조 구간(08월) */}
              <rect x="540" y="30" width="60" height="170" rx="6" fill="url(#gwband)" />
              <path d="M60,112 L186,90 L312,101 L438,64 L570,41 L690,53 L690,200 L60,200 Z" fill="url(#gwarea)" />
              <polyline
                points="60,112 186,90 312,101 438,64 570,41 690,53"
                fill="none"
                stroke="#2558E8"
                strokeWidth="2.6"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
              <circle cx="570" cy="41" r="6" fill="#fff" stroke="#2558E8" strokeWidth="2.6" />
              {/* 말풍선 */}
              <g fontFamily="Pretendard">
                <rect x="424" y="-14" width="132" height="52" rx="10" fill="#fff" stroke="#E6EAF2" />
                <text x="438" y="6" fontSize="14" fontWeight="600" fill="#20242B">
                  2026년 8월
                </text>
                <text x="438" y="28" fontSize="17" fontWeight="700" fill="#2558E8">
                  112
                  <tspan fontSize="13" fontWeight="500" fill="#9AA1AC">
                    {' '}
                    건 문의
                  </tspan>
                </text>
              </g>
              <g fontSize="14" fill="#9AA1AC" fontFamily="Pretendard" textAnchor="middle">
                <text x="60" y="224">04월</text>
                <text x="186" y="224">05월</text>
                <text x="312" y="224">06월</text>
                <text x="438" y="224">07월</text>
                <text x="570" y="224" fill="#2558E8" fontWeight="700">
                  08월
                </text>
                <text x="690" y="224">09월</text>
              </g>
            </svg>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">박스 라인업별 매출 구성</div>
              <div className="card-sub">이번 달 누적</div>
            </div>
          </div>
          <div className="donut-row">
            <div className="donut">
              <svg viewBox="0 0 140 140" width="170" height="170">
                <circle cx="70" cy="70" r={DONUT_R} fill="none" stroke="#EEF1F6" strokeWidth="12" />
                {(() => {
                  let acc = 0;
                  return DONUT_SEGMENTS.map((seg) => {
                    const len = (seg.pct / 100) * DONUT_LEN;
                    const el = (
                      <circle
                        key={seg.name}
                        cx="70"
                        cy="70"
                        r={DONUT_R}
                        fill="none"
                        stroke={seg.color}
                        strokeWidth="12"
                        strokeDasharray={`${Math.max(len - DONUT_GAP, 1)} ${DONUT_LEN}`}
                        strokeDashoffset={-acc}
                        transform="rotate(-90 70 70)"
                      />
                    );
                    acc += len;
                    return el;
                  });
                })()}
              </svg>
              <div className="donut-center">
                <div className="l">전체 매출</div>
                <div className="v">
                  {orders ? formatManwon(monthlyRevenue) : '4,820'}
                  <small>만원</small>
                </div>
              </div>
            </div>
            <div className="legend">
              {DONUT_SEGMENTS.map((seg) => (
                <div className="lg-item" key={seg.name}>
                  <span className="lg-dot" style={{ background: seg.color }}></span>
                  <span className="lg-name">{seg.name}</span>
                  <span className="lg-val">{seg.value}</span>
                  <span className="lg-pct">{seg.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ORDER STAGES + BAR ROW */}
      <div className="section grid-2">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">진행 중인 주문 — 공정 단계</div>
              <div className="card-sub">전체 {orders?.length ?? 0}건 · 단계별 현황</div>
            </div>
            <span className="card-tag">실시간 · 자동 갱신</span>
          </div>
          <div className="stages">
            <div className="stage done">
              <div className="n">1. 상담</div>
              <div className="count">42</div>
            </div>
            <div className="stage done">
              <div className="n">2. 시안</div>
              <div className="count">38</div>
            </div>
            <div className="stage done">
              <div className="n">3. 샘플</div>
              <div className="count">31</div>
            </div>
            <div className="stage now">
              <div className="n">4. 생산</div>
              <div className="count">14</div>
            </div>
            <div className="stage">
              <div className="n">5. 납품</div>
              <div className="count">9</div>
            </div>
            <div className="stage">
              <div className="n">6. 완료</div>
              <div className="count">5</div>
            </div>
          </div>
          <div className="bars" style={{ borderTop: '1px dashed var(--line)', paddingTop: 16 }}>
            <div className="bar-row">
              <span className="name">
                <span className="swatch c1"></span>자석박스
              </span>
              <span className="track">
                <span className="fill c1" style={{ width: '92%' }}>
                  29건
                </span>
              </span>
              <span className="val">29</span>
            </div>
            <div className="bar-row">
              <span className="name">
                <span className="swatch c2"></span>단상자
              </span>
              <span className="track">
                <span className="fill c2" style={{ width: '64%' }}>
                  20건
                </span>
              </span>
              <span className="val">20</span>
            </div>
            <div className="bar-row">
              <span className="name">
                <span className="swatch c3"></span>싸바리
              </span>
              <span className="track">
                <span className="fill c3" style={{ width: '38%' }}>
                  12건
                </span>
              </span>
              <span className="val">12</span>
            </div>
            <div className="bar-row">
              <span className="name">
                <span className="swatch c4"></span>쇼핑백
              </span>
              <span className="track">
                <span className="fill c4" style={{ width: '22%' }}>
                  7건
                </span>
              </span>
              <span className="val">7</span>
            </div>
            <div className="bar-row">
              <span className="name">
                <span className="swatch c5"></span>바인더·카드
              </span>
              <span className="track">
                <span className="fill c5" style={{ width: '14%' }}>
                  4건
                </span>
              </span>
              <span className="val">4</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">신규 견적 문의</div>
              <div className="card-sub">최근 접수 순 · {newInquiryCount}건</div>
            </div>
            <span className="card-tag">{newInquiryCount}건 대기</span>
          </div>
          <div className="bars">
            {recentInquiries.length === 0 && <div className="emptyRow">접수된 견적문의가 없습니다.</div>}
            {recentInquiries.map((inquiry) => (
              <div className="bar-row" key={inquiry.id}>
                <span className="name" title={inquiry.title ?? inquiry.shape ?? ''}>
                  {inquiry.title ?? inquiry.shape}
                </span>
                <span className="track">
                  <span className="fill" style={{ width: `${Math.max(10, Math.round((inquiry.quantity / maxInquiryQty) * 100))}%` }}>
                    {inquiry.quantity.toLocaleString()}개
                  </span>
                </span>
                <span className="val">{inquiry.quantity.toLocaleString()}</span>
              </div>
            ))}
          </div>
          <div
            style={{
              marginTop: 18,
              padding: '14px 16px',
              background: 'var(--brand-cream)',
              borderRadius: 12,
              border: '1px solid var(--brand-primary-100)',
            }}
          >
            <div
              style={{
                fontSize: 12,
                color: 'var(--brand-primary-700)',
                fontWeight: 700,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
              }}
            >
              오늘의 추천 제안
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-700)', marginTop: 6, lineHeight: 1.55 }}>
              <strong style={{ color: 'var(--ink-900)' }}>에코 팩 라인업</strong> 비중 확대 시 신규 고객사와의 계약
              확률 <strong style={{ color: 'var(--up)' }}>+18%</strong> 예상
            </div>
          </div>
        </div>
      </div>

      {/* RECENT ORDERS TABLE */}
      <div className="section">
        <div className="card" style={{ padding: 0 }}>
          <div className="card-head" style={{ padding: '20px 22px 14px', margin: 0, borderBottom: '1px solid var(--line-soft)' }}>
            <div>
              <div className="card-title">최근 주문 현황</div>
              <div className="card-sub">최근 등록 순</div>
            </div>
            <div className="sec-actions">
              <button className="btn btn-outline" type="button" onClick={() => navigate('/admin/orders')}>
                전체 보기
              </button>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>주문번호</th>
                  <th>고객명</th>
                  <th>제품 구성</th>
                  <th>수량</th>
                  <th>연락처</th>
                  <th style={{ textAlign: 'right' }}>계약 금액</th>
                  <th>상태</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length === 0 && (
                  <tr>
                    <td colSpan={7} className="emptyRow">
                      {orders ? '등록된 주문이 없습니다.' : '불러오는 중...'}
                    </td>
                  </tr>
                )}
                {recentOrders.map((order, index) => (
                  <tr key={order.id} onClick={() => navigate(`/admin/orders/${order.id}`)}>
                    <td>
                      <span className="idcell">{order.order_no}</span>
                    </td>
                    <td>
                      <div className="co">
                        <div className={`thumb ${THUMB_CLASSES[index % THUMB_CLASSES.length]}`}>
                          {order.customer_name.slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                          <strong>{order.customer_name}</strong>
                          <span>{formatPhone(order.phone)}</span>
                        </div>
                      </div>
                    </td>
                    <td>{order.shape ?? '-'}</td>
                    <td>{order.quantity.toLocaleString()}</td>
                    <td>{formatPhone(order.phone)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="price">
                        {order.total_price != null ? `₩ ${order.total_price.toLocaleString()}` : '미정'}
                      </span>
                    </td>
                    <td>
                      <span className={`badgePill ${ORDER_STATUS_BADGE[order.status]}`}>
                        <span className="pulse"></span> {ORDER_STATUS_LABEL[order.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* PORTFOLIO (숨김 처리 — SHOW_RECENT_PORTFOLIO) */}
      {SHOW_RECENT_PORTFOLIO && (
      <div className="section">
        <div className="sec-head">
          <div className="sec-title">최근 등록된 포트폴리오</div>
          <div className="sec-actions">
            <button className="btn btn-outline" type="button" onClick={() => navigate('/portfolio')}>
              전체 보기
            </button>
          </div>
        </div>
        <div className="portfolio-grid">
          {recentPortfolio.map((item, index) => (
            <div className="pf" key={item.id} onClick={() => navigate(`/portfolio/${item.id}`)}>
              <div className={`img ${PORTFOLIO_IMG_CLASSES[index % PORTFOLIO_IMG_CLASSES.length]}`}>{item.deco}</div>
              <div className="body">
                <div className="pg">{item.brand}</div>
                <div className="ttl">{item.name}</div>
                <div className="meta">
                  <span>{item.specTag}</span>
                  <strong>조회 {item.viewCount.toLocaleString()}</strong>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </AdminLayout>
  );
}

export default AdminDashboard;
